import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Check, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PlanComparison } from "@/components/PlanComparison";

export default function BillingPage() {
  const { biz } = useOutletContext();
  const [plans, setPlans] = useState([]);
  const [yearly, setYearly] = useState(false);
  const [loadingKey, setLoadingKey] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
    api.get("/business/billing-summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const subscribe = async (lookupKey) => {
    setLoadingKey(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout");
      setLoadingKey(null);
    }
  };

  const currentPlan = biz?.plan;
  const isActive = biz?.subscription_status === "active";

  return (
    <div className="space-y-8" data-testid="billing-page">
      <div>
        <h1 className="text-3xl text-foreground mb-1">Billing & Plans</h1>
        <p className="text-muted-foreground text-sm">Choose the plan that fits your guest environment.</p>
      </div>

      {isActive && (
        <div className="bg-ink text-cream rounded-md p-5 flex items-center justify-between" data-testid="current-plan-banner">
          <div>
            <p className="text-gold uppercase tracking-widest text-xs mb-1">Current plan</p>
            <p className="font-serif text-xl capitalize">{currentPlan} Hub · Active</p>
          </div>
          <Sparkles className="w-6 h-6 text-gold" />
        </div>
      )}

      {summary && (
        <div className="border border-border rounded-md p-5 bg-card" data-testid="billing-summary">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Your monthly bill</p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground capitalize">{summary.plan || "No"} plan (base)</span><span className="text-foreground">£{summary.base}/mo</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Rooms add-on ({summary.rooms} × £{summary.per_room_rate})</span><span className="text-foreground">£{summary.per_room_total}/mo</span></div>
            <div className="flex justify-between border-t border-border pt-2 mt-1 font-medium"><span className="text-foreground">Effective total</span><span className="text-accent" data-testid="billing-effective">£{summary.effective_monthly}/mo</span></div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Each room you add in the Rooms manager adds £{summary.per_room_rate}/month, billed automatically on your subscription.</p>
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="inline-flex items-center gap-2 bg-secondary rounded-full p-1">
          <button onClick={() => setYearly(false)} data-testid="billing-toggle-monthly" className={`px-4 py-1.5 rounded-full text-sm ${!yearly ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Monthly</button>
          <button onClick={() => setYearly(true)} data-testid="billing-toggle-yearly" className={`px-4 py-1.5 rounded-full text-sm ${yearly ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Yearly</button>
        </div>
        {yearly && <span className="text-xs text-accent">Save 2 months</span>}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((p) => {
          const lookupKey = yearly ? p.yearly : p.monthly;
          const isCurrent = isActive && currentPlan === p.id;
          return (
            <div key={p.id} className={`rounded-md border p-6 flex flex-col ${p.popular ? "border-accent shadow-[0_8px_32px_rgba(0,0,0,0.06)]" : "border-border"}`} data-testid={`plan-${p.id}`}>
              {p.popular && <Badge className="self-start bg-accent text-accent-foreground border-0 mb-3">Most popular</Badge>}
              <h3 className="font-serif text-2xl text-foreground">{p.name}</h3>
              <div className="my-4">
                <span className="text-4xl font-serif text-foreground">{p.currency}{yearly ? p.price_yearly : p.price_monthly}</span>
                <span className="text-muted-foreground text-sm">/{yearly ? "year" : "month"}</span>
              </div>
              <ul className="space-y-2.5 text-sm text-muted-foreground mb-6 flex-1">
                {p.features.map((f) => <li key={f} className="flex gap-2"><Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />{f}</li>)}
              </ul>
              <Button
                disabled={isCurrent || loadingKey === lookupKey}
                onClick={() => subscribe(lookupKey)}
                data-testid={`subscribe-${p.id}`}
                className={`w-full ${p.popular ? "bg-accent text-accent-foreground hover:opacity-90" : "bg-primary text-primary-foreground"}`}
              >
                {loadingKey === lookupKey ? <Loader2 className="w-4 h-4 animate-spin" /> : isCurrent ? "Current plan" : "Subscribe"}
              </Button>
            </div>
          );
        })}
      </div>
      <PlanComparison plans={plans} currentPlan={isActive ? currentPlan : null} />
      <p className="text-xs text-muted-foreground">Test mode — use card 4242 4242 4242 4242, any future expiry and CVC.</p>
    </div>
  );
}
